// Client-side 80G receipt + CSV export helpers (no backend required).

export type ReceiptData = {
  id: string;
  date: string;
  donor: string;
  pan?: string;
  amount: number;
  purpose: string;
  beneficiary: string;
  frequency?: string;
  cycle?: string;
  donationType?: "monetary" | "inkind";
  itemName?: string;
  quantity?: string;
  dispatchStatus?: string;
};

const inr = (n: number) => `Rs ${n.toLocaleString("en-IN")}`;

function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function receiptHtml(r: ReceiptData) {
  const isInKind = r.donationType === "inkind" || !!r.itemName;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${r.id}</title>
<style>
body{font-family:ui-sans-serif,system-ui,Arial;margin:0;padding:40px;color:#2a1a2e;background:#fbf7f1}
.card{max-width:720px;margin:auto;background:#fff;border:2px solid #5b1230;border-radius:18px;padding:36px;box-shadow:0 10px 30px rgba(0,0,0,0.05)}
.header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #5b1230;padding-bottom:18px}
h1{font-size:22px;margin:0;color:#5b1230}
.badge{background:#eec643;color:#2a1a2e;font-size:11px;font-weight:bold;padding:4px 10px;border-radius:20px;text-transform:uppercase}
.muted{color:#7a6a72;font-size:12px;margin-top:4px}
table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
td{padding:10px 0;border-bottom:1px solid #eee4d8}
td:last-child{text-align:right;font-weight:600}
.total{font-size:22px;color:#5b1230;font-weight:700}
.stamps{margin-top:24px;display:flex;justify-content:space-between;align-items:center;background:#faf3eb;padding:14px;border-radius:12px;font-size:11px;color:#5b1230}
.foot{margin-top:20px;font-size:11px;color:#7a6a72;line-height:1.6}
@media print{body{background:#fff;padding:0}.card{border:1px solid #000}}
</style></head><body><div class="card">
<div class="header">
  <div>
    <h1>Daan Amrut Foundation</h1>
    <p class="muted">Official ${isInKind ? "In-Kind Material Seva Receipt" : "80G Tax Exemption Certificate"} &middot; Ahmedabad, Gujarat</p>
  </div>
  <span class="badge">Verified 80G</span>
</div>
<table>
<tr><td>Receipt Number</td><td>${r.id}</td></tr>
<tr><td>Date of Payment</td><td>${r.date}</td></tr>
<tr><td>Donor Name</td><td>${r.donor}</td></tr>
${r.pan ? `<tr><td>PAN Number (Section 80G)</td><td>${r.pan}</td></tr>` : ""}
<tr><td>Purpose / Cause</td><td>${r.purpose}</td></tr>
<tr><td>Beneficiary Gaushala / NGO</td><td>${r.beneficiary}</td></tr>
${isInKind ? `<tr><td>In-Kind Package</td><td>${r.quantity ?? ""} ${r.itemName ?? ""}</td></tr>` : ""}
${isInKind ? `<tr><td>Material Dispatch Status</td><td>${r.dispatchStatus ?? "Delivered / Assigned to Local Vendor"}</td></tr>` : ""}
${r.frequency ? `<tr><td>Frequency</td><td>${r.frequency}</td></tr>` : ""}
<tr><td>Total Amount Paid</td><td class="total">${inr(r.amount)}</td></tr>
</table>
<div class="stamps">
  <div>
    <strong>80G Reg No:</strong> 80G/AHM/2021/4412<br>
    <strong>12A Reg No:</strong> 12A/AHM/2020/3309
  </div>
  <div style="text-align:right">
    <strong>Verification Code:</strong> QR-DA-80G-${r.id.slice(-6)}<br>
    <em>50% Tax Deduction under Sec 80G(5)</em>
  </div>
</div>
<p class="foot">This receipt certifies that the donation is eligible for deduction under Section 80G of the Income Tax Act, 1961. Funds and materials are 100% routed to the verified gaushala after platform audit. This is an official computer-generated certificate needing no physical signature.</p>
</div>
<script>window.onload=function(){setTimeout(function(){window.print()},400)}<\/script>
</body></html>`;
}

/** Downloads a printable 80G receipt (open it and use Save as PDF). */
export function downloadReceipt(r: ReceiptData) {
  download(`Receipt-${r.id}.html`, receiptHtml(r), "text/html;charset=utf-8");
}

/** Opens the receipt in a new tab and triggers the print / save-as-PDF dialog. */
export function printReceipt(r: ReceiptData) {
  const w = window.open("", "_blank");
  if (!w) return downloadReceipt(r);
  w.document.write(receiptHtml(r));
  w.document.close();
}

export function downloadCsv(filename: string, rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  download(filename, rows.map((r) => r.map(esc).join(",")).join("\n"), "text/csv;charset=utf-8");
}